/** Get nested value from object by path (e.g. "currentPatient.isSSOLogin") */
function getObjValue<T = unknown>(
  obj: Record<string, unknown> | null | undefined,
  path: string,
  defaultValue?: T
): T {
  if (obj == null) return defaultValue as T
  const keys = path.split('.')
  let current: unknown = obj
  for (const key of keys) {
    if (current == null || typeof current !== 'object') return defaultValue as T
    current = (current as Record<string, unknown>)[key]
  }
  return (current === undefined ? defaultValue : current) as T
}

/**
 * Returns the profile from localStorage (key: portal-profile).
 * When the value is from Zustand persist it has shape { state, version }; we return state
 * so that profile.profileAuthInfo and profile.currentPatient work as expected.
 */
export function getProfile(): Record<string, unknown> | null {
  try {
    const raw = localStorage.getItem('portal-profile')
    if (!raw) return null
    const parsed = JSON.parse(raw) as Record<string, unknown>
    if (parsed && typeof parsed.state === 'object' && parsed.state !== null) {
      return parsed.state as Record<string, unknown>
    }
    return parsed
  } catch {
    return null
  }
}

export function hasValidProfile(profile: Record<string, unknown> | null): boolean {
  if (!profile) return false
  const hasProfile = profile.profileAuthInfo != null
  const hasCurrentPatient = profile.currentPatient != null
  return hasProfile || hasCurrentPatient
}

export function isSSOUser(profile: Record<string, unknown> | null): boolean {
  return getObjValue(profile, 'currentPatient.isSSOLogin', false) as boolean
}

/**
 * Single gate for “logged in enough for dashboard / ProtectedRoute”.
 * Must stay in sync with ProtectedRoute and RedirectIfAuth to avoid / ↔ /account/login redirect loops.
 */
export function isFullyAuthenticatedForApp(
  profileAuthInfoFromStore: unknown,
  currentPatientFromStore: unknown
): boolean {
  const token = sessionStorage.getItem('config')
  const completeAuth = sessionStorage.getItem('completeAuth')
  if (!token || !completeAuth) return false

  const profile = getProfile()
  if (profile && isSSOUser(profile)) return false

  const hasStorage = hasValidProfile(profile)
  const hasStore = profileAuthInfoFromStore != null || currentPatientFromStore != null
  return hasStorage || hasStore
}
